// Shared UI bits for the External Tournaments pages (client/DESIGN.md patterns).
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const card = {
  background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)',
  padding: '1.1rem 1.25rem', marginBottom: '1rem',
};
export const h1 = { margin: 0, fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-display, sans-serif)' };
export const h2 = { margin: '0 0 0.25rem', fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display, sans-serif)' };
export const sub = { margin: '0 0 0.9rem', fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5 };
export const label = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.01em', marginBottom: '0.35rem' };
export const sectionLabel = { fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' };
export const mono = { fontFamily: 'var(--font-mono, monospace)', fontSize: '0.78rem' };
export const input = {
  padding: '0.55rem 0.85rem', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontSize: '0.85rem',
  background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
export const codeBox = {
  ...mono, background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)',
  padding: '0.55rem 0.7rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0,
};

export const RGB = { blue: '59,130,246', green: '34,197,94', red: '239,68,68', amber: '245,158,11', violet: '139,92,246', gray: '148,163,184', cyan: '6,182,212' };
export function btn(color = RGB.blue, solid = false) {
  return {
    padding: '0.38rem 0.8rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 600, fontSize: '0.76rem',
    background: solid ? `rgba(${color},0.22)` : `rgba(${color},0.12)`, border: `1px solid rgba(${color},${solid ? 0.45 : 0.25})`,
    color: `rgb(${color})`, display: 'inline-flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap', fontFamily: 'inherit',
  };
}
export function pill(color = RGB.gray) {
  return { padding: '0.15rem 0.5rem', borderRadius: 9999, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.03em',
    background: `rgba(${color},0.1)`, color: `rgb(${color})`, border: `1px solid rgba(${color},0.15)`, whiteSpace: 'nowrap' };
}
export const STATUS_COLOR = { live: RGB.green, upcoming: RGB.blue, finished: RGB.gray, cancelled: RGB.red, warmup: RGB.amber, paused: RGB.amber, halftime: RGB.violet, knife: RGB.cyan };

export const Icon = ({ d, size = 13, stroke = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);
export const ICONS = {
  upload: 'M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2M12 15V4M7 9l5-5 5 5',
  copy: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
  check: 'M5 13l4 4L19 7',
  refresh: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  plus: 'M12 4v16m8-8H4',
  trash: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  link: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  unlink: 'M18.364 5.636l-12.728 12.728M8.464 8.464L5.636 5.636m12.728 12.728l-2.828-2.828',
  server: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01',
  bell: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
  download: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
  back: 'M15 19l-7-7 7-7',
  hook: 'M13 10V3L4 14h7v7l9-11h-7z',
  edit: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
};

export function CopyButton({ text, small }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch (_) {
      const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (__) { /* ignore */ } document.body.removeChild(ta);
    }
    setDone(true); setTimeout(() => setDone(false), 1800);
  };
  return (
    <button type="button" onClick={copy} style={{ ...btn(done ? RGB.green : RGB.blue), ...(small ? { padding: '0.25rem 0.55rem' } : {}) }}>
      <Icon d={done ? ICONS.check : ICONS.copy} /> {done ? t('ext.copied', 'Copied') : t('ext.copy', 'Copy')}
    </button>
  );
}

/** Two-step confirm (DESIGN: destructive / rotating actions confirm first). */
export function ConfirmButton({ label: text, warning, onConfirm, color = RGB.red, icon }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!open) return <button type="button" style={btn(color)} onClick={() => setOpen(true)}>{icon && <Icon d={icon} />} {text}</button>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
      <span style={pill(color)}>{warning}</span>
      <button type="button" disabled={busy} style={btn(color, true)} onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); setOpen(false); } }}>
        {busy ? t('common.loading', 'Loading...') : t('ext.confirm', 'Confirm')}
      </button>
      <button type="button" style={btn(RGB.gray)} onClick={() => setOpen(false)}>{t('common.cancel', 'Cancel')}</button>
    </span>
  );
}

// value = file in /killfeed (".png" added unless it has its own extension)
const ICON_ALIAS = { m4a1_silencer_off: 'm4a1_silencer', usp_silencer_off: 'usp_silencer', molotov: 'molotov', incgrenade: 'incgrenade', smokegrenade: 'smokegrenade.svg' };
/** Weapon / utility icon from /killfeed (falls back to the name). */
export function WeaponIcon({ name, height = 14, title }) {
  const [broken, setBroken] = useState(false);
  if (!name) return null;
  const file = ICON_ALIAS[name] !== undefined ? ICON_ALIAS[name] : name;
  if (!file || broken) return <span style={{ ...mono, fontSize: '0.66rem', color: 'var(--text-secondary)' }} title={title || name}>{name.replace(/grenade$/, '')}</span>;
  return <img src={`/killfeed/${/\.\w+$/.test(file) ? file : `${file}.png`}`} alt={name} title={title || name} onError={() => setBroken(true)} style={{ height, opacity: 0.9, filter: 'brightness(1.1)' }} />;
}

export function errMsg(e, fallback) {
  return (e && e.response && e.response.data && (e.response.data.error && (e.response.data.error.message || e.response.data.error))) || fallback;
}

// The panel's only rating: HLTV Rating 2.0 (estimate; same formula on panel and ext matches).
export const ratingOf = (p) => Number((p && (p.hltv_rating ?? p.rating)) ?? 0);
export const ratingColor = (v) => (v >= 1.1 ? 'var(--accent-green)' : v < 0.9 ? 'var(--accent-red)' : undefined);
/** Rating column header text. */
export function RatingLabel() {
  return <span style={{ color: 'var(--accent-cyan, #06b6d4)' }}>HLTV 2.0</span>;
}

export function timeAgo(iso) {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}
